import { useEffect, useRef, useState } from 'react';
import { Button, Field, FieldTitle, Spinner } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { FONT_ACCEPTED_FILE_TYPES, FONT_MIME_TYPES } from '@inference.engine/file-utils/web';

import { MEDIA_ACCEPTED_FILE_TYPES, type MediaUpload } from '@/entities/brand-media';
import type { FontUpload } from '@/entities/brand-font';
import { FormSidePanel } from '@/shared/ui/layout';
import { FileDropzone } from '@/shared/ui/FileDropzone';
import { FileListItem } from '@/shared/ui/FileListItem';

const ACCEPTED_FILE_TYPES: Record<string, string[]> = {
  ...MEDIA_ACCEPTED_FILE_TYPES,
  ...FONT_ACCEPTED_FILE_TYPES,
};

const MAX_SIZE = 100 * 1024 * 1024;

const isFont = (file: File): boolean => (FONT_MIME_TYPES as readonly string[]).includes(file.type);

/**
 * `crypto.randomUUID` is unavailable in non-secure-context browsers. Good enough for an ephemeral
 * chip id -- it never leaves the page.
 */
function generateClientId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

interface Chip {
  id: string;
  file: File;
  kind: 'media' | 'font';
}

/**
 * One picker for everything the library takes: images, video and fonts together, so a person
 * never has to know in advance which upload flow a file belongs to.
 *
 * A drop stages chips rather than uploading straight away: a person sees what they are about to
 * add and can remove one before anything is sent, then confirms with Upload. `mediaUpload` and
 * `fontUpload` only report an aggregate `isUploading`/`failure` (no per-file status), so a
 * confirmed batch clears its chips immediately rather than trying to track which one is still in
 * flight -- the spinner below speaks for the whole batch. The sheet closes itself once that batch
 * finishes with no failure; a failure leaves it open with the message, chips already cleared,
 * ready for another drop.
 *
 * An edge panel rather than a centred dialog because a batch has no upper bound. A dialog grew
 * with the list until it filled the window and pushed its own drop target and buttons out of
 * reach; a full-height panel gives the list somewhere to scroll while what acts on it stays put.
 */
export const UploadAssetsSheet = ({
  open,
  onClose,
  mediaUpload,
  fontUpload,
}: {
  open: boolean;
  onClose: () => void;
  mediaUpload: MediaUpload;
  fontUpload: FontUpload;
}) => {
  const { t } = useTranslation('tools');

  const [chips, setChips] = useState<Chip[]>([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  // Set once `isUploading` is actually observed true after a submit, so the effect below can tell
  // a real start-to-finish transition from `hasSubmitted` simply being true while nothing has
  // happened yet (e.g. an upload hook whose `isUploading` hasn't flipped on this tick).
  const startedRef = useRef(false);

  const isUploading = mediaUpload.isUploading || fontUpload.isUploading;
  const failure = fontUpload.failure ?? mediaUpload.failure;

  // Aggregate hooks only report the batch's own start/end, not per-file progress, so this is the
  // one place that can tell "a submitted batch just finished" from "nothing has happened yet".
  useEffect(() => {
    if (!hasSubmitted) return;
    if (isUploading) {
      startedRef.current = true;
      return;
    }
    if (!startedRef.current) return;
    startedRef.current = false;
    setHasSubmitted(false);
    if (failure === null) onClose();
  }, [hasSubmitted, isUploading, failure, onClose]);

  const closeAndReset = () => {
    setChips([]);
    onClose();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !isUploading) closeAndReset();
  };

  const onDrop = (accepted: File[]) => {
    const added: Chip[] = accepted.map((file) => ({
      id: generateClientId(),
      file,
      kind: isFont(file) ? 'font' : 'media',
    }));
    setChips((prev) => [...prev, ...added]);
  };

  const removeChip = (id: string) => setChips((prev) => prev.filter((chip) => chip.id !== id));

  const handleUpload = () => {
    const fontFiles = chips.filter((chip) => chip.kind === 'font').map((chip) => chip.file);
    const mediaFiles = chips.filter((chip) => chip.kind === 'media').map((chip) => chip.file);
    if (fontFiles.length > 0) fontUpload.upload(fontFiles);
    if (mediaFiles.length > 0) mediaUpload.upload(mediaFiles);
    setChips([]);
    setHasSubmitted(true);
  };

  return (
    <FormSidePanel
      open={open}
      onOpenChange={handleOpenChange}
      title={t('brandMedia.uploadSheet.title')}
      showCloseButton={!isUploading}
      dividers
      footer={
        <>
          <Button variant="outline" disabled={isUploading} onClick={closeAndReset}>
            {t('brandMedia.uploadSheet.cancel')}
          </Button>
          <Button disabled={chips.length === 0 || isUploading} onClick={handleUpload}>
            {chips.length > 0
              ? t('brandMedia.uploadSheet.uploadWithCount', { count: chips.length })
              : t('brandMedia.uploadSheet.upload')}
          </Button>
        </>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col gap-sm">
        {/* Pinned above the list rather than scrolling with it: with a long batch staged, the
            way to add more must not be somewhere you have to scroll back up to find.
            FieldTitle rather than FieldLabel: react-dropzone hides its input and the drop
            target names itself, so a <label> here would point at nothing. */}
        <Field className="shrink-0">
          <FieldTitle>{t('brandMedia.uploadSheet.filesLabel')}</FieldTitle>
          <FileDropzone
            className="shrink-0"
            accept={ACCEPTED_FILE_TYPES}
            maxSize={MAX_SIZE}
            disabled={isUploading}
            title={t('brandMedia.uploadSheet.dropFilesHere')}
            caption={t('brandMedia.uploadSheet.acceptedFormats')}
            onDrop={onDrop}
          />
        </Field>

        {chips.length > 0 && (
          <div
            data-testid="upload-staged-files"
            className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto"
          >
            {chips.map((chip) => (
              <FileListItem
                key={chip.id}
                fileName={chip.file.name}
                fileSize={chip.file.size}
                fileType={chip.file.type}
                onRemove={() => removeChip(chip.id)}
              />
            ))}
          </div>
        )}

        {isUploading && <Spinner />}
        {failure !== null && <p className="text-body-md text-destructive">{failure}</p>}
      </div>
    </FormSidePanel>
  );
};
